import * as Types from '../gql/_types.js';

import { UserModule } from "./init";
import { mapArrayToStateObject } from "src/utils/state";
import { Action, AsyncAction, GraphQLSubscriptionResultsPayload } from 'src/state/_types';
import { subscriptions } from 'src/state/gql/_operations';
import { handleGraphqQLErrorResponse, handleGraphqQLSubscriptionErrorResponse } from 'src/state/gql/utils';
import { SetMetaInput } from 'src/state/Module';

const Module = UserModule;

export const state = Module;

export const getItems: AsyncAction<Types.GetUsersQueryVariables> = async ({state, effects}, payload) => {
    let finalPayload = Module.mergeQueryPayload(payload);
    state.users.errors = [];
    state.users.loading = true;
    try {
        let { limit, offset, orderBy } = state.users.meta;
        let originalPayload = { limit, offset, order_by: orderBy };
        let { meta, items } = await effects.gql.queries.GetUsers({...originalPayload, ...finalPayload})
        state.users.meta.total = meta.aggregate?.count || 0;
        state.users.items = mapArrayToStateObject(items, 'id');
    } catch (e: any) {
        state.users.errors = handleGraphqQLErrorResponse(e);
    }
    state.users.loading = false;
}

export const resetState: Action = ({state}) => {
    state.users.errors = []
    state.users.loading = false
    state.users.items = {}
    state.users.currentItemId = null
}

export const resetMeta: Action = ({state}) => {
    state.users.meta = {
        limit: Module.defaultLimit,
        sortOrder: Module.defaultSortOrder,
        sortKey: Module.defaultSortKey,
        page: 1,
        total: 0,
        offset: 0,
        orderBy: {}
    }
}

export const resetModule: AsyncAction = async ({actions}) => {
    actions.users.resetMeta()
    actions.users.resetState()
}

export const getMoreItems: AsyncAction = async ({state, effects}) => {
    let finalPayload = Module.mergeQueryPayload({});
    state.users.errors = [];
    state.users.loadingMore = true;
    try {
        let { limit, orderBy, page } = state.users.meta;
        let newPage = ++page;
        let newOffset = ( limit * newPage ) - limit
        let originalPayload = { limit, offset: newOffset, order_by: orderBy };
        let { meta, items } = await effects.gql.queries.GetUsers({...originalPayload, ...finalPayload})
        state.users.meta.total = meta.aggregate?.count || 0;
        state.users.meta.page = newPage;
        state.users.meta.offset = newOffset;
        state.users.items = { ...state.users.items, ...mapArrayToStateObject(items, 'id') };
    } catch (e: any) {
        state.users.errors = handleGraphqQLErrorResponse(e);
    }
    state.users.loadingMore = false;
}

export const getAllItems: AsyncAction<Types.GetUsersQueryVariables> = async ({state, effects}, payload) => {
    state.users.errors = [];
    state.users.loading = true;
    try {
        let { meta, items } = await effects.gql.queries.GetUsers(payload)
        state.users.meta.total = meta.aggregate?.count || 0;
        state.users.items = mapArrayToStateObject(items, 'id');
    } catch (e: any) {
        state.users.errors = handleGraphqQLErrorResponse(e);
    }
    state.users.loading = false;
}

export const getItem: AsyncAction<Types.GetUserQueryVariables,Types.GetUserQuery['item']> = async ({state, effects}, payload) => {
    try {
        let { item } = await effects.gql.queries.GetUser(payload);
        return item;
    } catch (e: any) {
        state.users.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const loadItem: AsyncAction<Types.GetUserQueryVariables> = async ({state, actions}, payload) => {
    state.users.loading = true;
    try {
        let item = await actions.users.getItem(payload);
        if (item) {
            state.users.currentItemId = item.id;
            state.users.items[item.id] = item;
        }
    } catch (e: any) {
        state.users.errors = handleGraphqQLErrorResponse(e);
    }
    state.users.loading = false;
}

export const selectItem: Action<string> = async ({state}, payload) => {
    state.users.currentItemId = payload
}

export const deselectItem: Action = async ({state}) => {
    state.users.currentItemId = null
}

export const setMeta: Action<SetMetaInput<Types.UserFragment>> = async ({state}, meta) => {
    state.users.meta = {
        ...state.users.meta,
        ...meta
    }
}


export const createItem: AsyncAction<Types.CreateUserMutationVariables['object'],Types.CreateUserMutation['item']> = async ({state, effects}, payload) => {
    let finalPayload = Module.mergeCreatePayload<Types.CreateUserMutationVariables['object']>(payload);
    state.users.errors = [];
    try {
        let { item } = await effects.gql.mutations.CreateUser({object:finalPayload});
        return item;
    } catch (e: any) {
        state.users.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const updateItem: AsyncAction<Types.UpdateUserMutationVariables,Types.UpdateUserMutation['item']> = async ({state, effects}, payload) => {
    state.users.errors = [];
    try {
        let { item } = await effects.gql.mutations.UpdateUser(payload);
        return item;
    } catch (e: any) {
        state.users.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const deleteItem: AsyncAction<Types.DeleteUserMutationVariables,Types.DeleteUserMutation['item']> = async ({state, effects}, payload) => {
    state.users.errors = [];
    try {
        let { item } = await effects.gql.mutations.DeleteUser(payload);
        return item;
    } catch (e: any) {
        state.users.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const syncGetItemsMeta: Action<Types.SyncGetUsersMetaSubscriptionVariables> = async ({actions, effects}, {where}) => {
    effects.subscriptions.subscribe<Types.SyncGetUsersMetaSubscription, Types.SyncGetUsersMetaSubscriptionVariables>(
        'syncGetUsersMeta', 
        subscriptions.SyncGetUsersMeta, 
        { where }, 
        actions.users.onItemsMeta
    );
}

export const unsubItemsMeta: Action = ({effects}) => {
    effects.subscriptions.unsubscribe('syncGetUsersMeta');
}

export const onItemsMeta: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetUsersMetaSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.users.errors = [ ...state.users.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data) {
        state.users.meta.total = payload.data.meta.aggregate?.count || 0;
    }    
}

export const syncGetItems: Action<Types.SyncGetUsersSubscriptionVariables> = async ({state, actions, effects}, payload) => {
    let { limit, offset, orderBy } = state.users.meta;
    let originalPayload = { limit, offset, order_by: orderBy };
    state.users.loading = true;
    effects.subscriptions.subscribe<Types.SyncGetUsersSubscription, Types.SyncGetUsersSubscriptionVariables>(
        'syncGetUsers', 
        subscriptions.SyncGetUsers, 
        {...originalPayload, ...payload}, 
        actions.users.onItemsData,
        () => { state.users.loading = false }
    );
}

export const unsubItemsData: Action = ({state,effects}) => {
    effects.subscriptions.unsubscribe('syncGetUsers');
    state.users.items = {};
}

export const onItemsData: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetUsersSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.users.errors = [ ...state.users.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data&&payload.data.items) {
        state.users.items = mapArrayToStateObject(payload.data.items, 'id');
    }  
}

export const subscribeToItems: Action<Types.SyncGetUsersSubscriptionVariables> = async ({actions}, payload) => {
    let finalPayload = Module.mergeQueryPayload(payload);
    actions.users.syncGetItems(finalPayload);
    actions.users.syncGetItemsMeta(finalPayload);
}

export const unsubscribeFromItems: Action = ({actions}) => {
    actions.users.unsubItemsData();
    actions.users.unsubItemsMeta();
}

export const syncGetItem: Action<Types.SyncGetUserSubscriptionVariables> = async ({state, actions, effects}, payload) => {
    state.users.loading = true;
    effects.subscriptions.subscribe<Types.SyncGetUserSubscription, Types.SyncGetUserSubscriptionVariables>(
        'syncGetUser', 
        subscriptions.SyncGetUser, 
        payload, 
        actions.users.onItemData,
        () => { state.users.loading = false }
    );
}

export const unsubItemData: Action = ({state,effects}) => {
    effects.subscriptions.unsubscribe('syncGetUser');
    state.users.currentItemId = null;
}

export const onItemData: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetUserSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.users.errors = [ ...state.users.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data&&payload.data.item) {
        state.users.currentItemId = payload.data.item.id;
        state.users.items[payload.data.item.id] = payload.data.item;
    }
}

export const subscribeToItem: Action<Types.SyncGetUserSubscriptionVariables> = async ({actions}, payload) => {
    actions.users.syncGetItem(payload);
}

export const unsubscribeFromItem: Action = ({actions}) => {
    actions.users.unsubItemData();
}

export const actions = {
    getItems,
    getAllItems,
    resetState,
    resetMeta,
    getMoreItems,
    resetModule,
    getItem,
    loadItem,
    selectItem,
    deselectItem,
    setMeta,
    createItem,
    updateItem,
    deleteItem,
    syncGetItemsMeta,
    unsubItemsMeta,
    onItemsMeta,
    syncGetItems,
    unsubItemsData,
    onItemsData,
    subscribeToItems,
    unsubscribeFromItems,
    syncGetItem,
    unsubItemData,
    onItemData,
    subscribeToItem,
    unsubscribeFromItem
};